import React, { useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useGame } from '../../context/GameContext';
import { technologies, buildings } from '../../data';
import { WONDERS, WONDER_BALANCE, getEraName } from '../../data/wonders';

// Get era config from data (modular!)
const { eraIcons, eraBonuses, wonderEraNames } = WONDER_BALANCE;
const ERA_LIST = Object.keys(wonderEraNames).map(Number).sort((a, b) => a - b);

/**
 * AgeProgressBar - Shows current age/era progress at top of screen
 * Expandable to show full Civ6-style timeline with tooltips and detail panel
 */
function AgeProgressBar() {
  const { researchedTechs, builtWonders, buildingCounts } = useGame();
  const [expanded, setExpanded] = useState(false);
  const [hoveredEra, setHoveredEra] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [selectedEra, setSelectedEra] = useState(null);

  // Calculate current era and detailed stats
  const { currentEra, totalProgress, eraStats, eraDetails, totalTechs, totalWonders, totalBuildings } = useMemo(() => {
    const allTechs = Object.values(technologies);
    const allWonders = Object.values(WONDERS);
    const totalTechCount = allTechs.length;
    // Only count techs that actually exist in the technologies data
    const validResearchedTechs = researchedTechs.filter(id => technologies[id]);
    const completedTechs = Math.min(validResearchedTechs.length, totalTechCount);
    
    // Group by tier
    const techsByTier = {};
    const wondersByTier = {};
    const buildingsByTier = {};
    
    const maxEra = ERA_LIST[ERA_LIST.length - 1];
    
    ERA_LIST.forEach(i => {
      techsByTier[i] = allTechs.filter(t => t.tier === i);
      wondersByTier[i] = allWonders.filter(w => w.tier === i);
      buildingsByTier[i] = Object.values(buildings).filter(b => {
        const unlockTech = b.unlockCondition?.tech;
        if (!unlockTech) return i === 1;
        const tech = technologies[unlockTech];
        return tech?.tier === i;
      });
    });
    
    // Find highest tier with at least one tech researched
    let highestTier = 1;
    for (let i = maxEra; i >= 1; i--) {
      if (techsByTier[i]?.some(t => researchedTechs.includes(t.id))) {
        highestTier = i;
        break;
      }
    }
    
    const totalProgressPct = totalTechCount > 0 ? Math.min(100, (completedTechs / totalTechCount) * 100) : 0;
    
    // Stats and details for each era
    const stats = {};
    const details = {};
    
    ERA_LIST.forEach(i => {
      const tierTechs = techsByTier[i];
      const tierWonders = wondersByTier[i];
      const tierBuildings = buildingsByTier[i];
      
      const techsResearched = tierTechs.filter(t => researchedTechs.includes(t.id));
      const techsPending = tierTechs.filter(t => !researchedTechs.includes(t.id));
      const wondersBuilt = tierWonders.filter(w => builtWonders?.includes(w.id));
      const wondersPending = tierWonders.filter(w => !builtWonders?.includes(w.id));
      const buildingsOwned = tierBuildings.filter(b => (buildingCounts[b.id] || 0) > 0);
      
      stats[i] = {
        techs: { done: techsResearched.length, total: tierTechs.length },
        wonders: { done: wondersBuilt.length, total: tierWonders.length },
        buildings: { done: buildingsOwned.length, total: tierBuildings.length },
        percent: tierTechs.length > 0 ? Math.min(100, (techsResearched.length / tierTechs.length) * 100) : 0,
        unlocked: i <= highestTier,
        current: i === highestTier,
        complete: tierTechs.length > 0 && techsResearched.length === tierTechs.length,
      };
      
      details[i] = {
        techsResearched,
        techsPending,
        wondersBuilt,
        wondersPending,
        buildingsOwned,
        allBuildings: tierBuildings,
        allWonders: tierWonders,
      };
    });
    
    const builtBuildingsCount = Object.values(buildingCounts).reduce((a, b) => a + b, 0);
    
    return {
      currentEra: highestTier,
      totalProgress: totalProgressPct,
      eraStats: stats,
      eraDetails: details,
      totalTechs: { done: validResearchedTechs.length, total: totalTechCount },
      totalWonders: { done: builtWonders?.length || 0, total: WONDER_BALANCE.maxTotal },
      totalBuildings: builtBuildingsCount,
    };
  }, [researchedTechs, builtWonders, buildingCounts]);

  // Hover handlers
  const handleEraHover = (era, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPos({ 
      x: rect.left + rect.width / 2, 
      y: rect.bottom + 8 
    });
    setHoveredEra(era);
  };

  const handleEraLeave = () => {
    setHoveredEra(null);
  };

  // Click handler
  const handleEraClick = (era) => {
    setSelectedEra(selectedEra === era ? null : era);
  };

  return (
    <div className="age-progress-container">
      {/* Collapsed Bar */}
      <div 
        className="age-progress-bar"
        onClick={() => {
          setExpanded(!expanded);
          if (expanded) setSelectedEra(null);
        }}
      >
        <div className="apb-left">
          <span className="apb-era-icon">{eraIcons[currentEra]}</span>
          <span className="apb-era-name">{getEraName(currentEra)}</span>
        </div>
        
        <div className="apb-center">
          <div className="apb-track">
            <div 
              className="apb-fill"
              style={{ width: `${totalProgress}%` }}
            />
            {ERA_LIST.map(era => (
              <div 
                key={era}
                className={`apb-marker ${eraStats[era].unlocked ? 'unlocked' : ''} ${eraStats[era].current ? 'current' : ''}`}
                style={{ left: `${((era - 1) / (ERA_LIST.length - 1)) * 100}%` }}
                title={getEraName(era)}
              />
            ))}
          </div>
        </div>
        
        <div className="apb-right">
          <span className="apb-percent">{Math.floor(totalProgress)}%</span>
          <span className="apb-expand">{expanded ? '▼' : '▶'}</span>
        </div>
      </div>

      {/* Expanded Timeline */}
      {expanded && (
        <div className="age-timeline">
          <div className="at-header">
            <h3>🏛️ Civilization Progress</h3>
            <div className="at-total">
              <span className="at-total-item">
                <span className="icon">🔬</span>
                <span className="value">{totalTechs.done}</span>/{totalTechs.total}
              </span>
              <span className="at-total-item">
                <span className="icon">🏛️</span>
                <span className="value">{totalWonders.done}</span>/{totalWonders.total}
              </span>
              <span className="at-total-item">
                <span className="icon">🏗️</span>
                <span className="value">{totalBuildings}</span>
              </span>
            </div>
          </div>
          
          <div className="at-eras-wrapper">
            <div 
              className="at-eras"
              style={{ '--progress-width': `${(currentEra / ERA_LIST.length) * 100}%` }}
            >
              {ERA_LIST.map(era => {
                const stats = eraStats[era];
                
                return (
                  <div 
                    key={era}
                    className={`at-era ${stats.unlocked ? 'unlocked' : 'locked'} ${stats.current ? 'current' : ''} ${stats.complete ? 'complete' : ''} ${selectedEra === era ? 'selected' : ''}`}
                    onMouseEnter={(e) => handleEraHover(era, e)}
                    onMouseLeave={handleEraLeave}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEraClick(era);
                    }}
                  >
                    <div className="at-era-node">
                      {eraIcons[era]}
                      <span className="at-era-badge">
                        {stats.complete ? '✓' : stats.current ? era : stats.unlocked ? era : '🔒'}
                      </span>
                    </div>
                    
                    <div className="at-era-info">
                      <div className="at-era-name">{getEraName(era)}</div>
                      
                      <div className="at-era-stats">
                        <div className="at-era-stat techs">
                          <span className="icon">🔬</span>
                          <span className="count">{stats.techs.done}</span>/{stats.techs.total}
                        </div>
                        {stats.wonders.total > 0 && (
                          <div className="at-era-stat wonders">
                            <span className="icon">🏛️</span>
                            <span className="count">{stats.wonders.done}</span>/{stats.wonders.total}
                          </div>
                        )}
                      </div>
                      
                      <div className="at-era-minibar">
                        <div 
                          className="at-era-minibar-fill"
                          style={{ width: `${stats.percent}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Selected Era Detail Popup */}
      {selectedEra && (
        <EraDetailPanel 
          era={selectedEra}
          stats={eraStats[selectedEra]}
          details={eraDetails[selectedEra]}
          icon={eraIcons[selectedEra]}
          bonus={eraBonuses[selectedEra]}
          onClose={() => setSelectedEra(null)}
        />
      )}

      {/* Hover Tooltip */}
      {hoveredEra && !selectedEra && ReactDOM.createPortal(
        <EraTooltip 
          era={hoveredEra}
          stats={eraStats[hoveredEra]}
          details={eraDetails[hoveredEra]}
          icon={eraIcons[hoveredEra]}
          bonus={eraBonuses[hoveredEra]}
          x={tooltipPos.x}
          y={tooltipPos.y}
        />,
        document.body
      )}
    </div>
  );
}

/**
 * Era Tooltip - Shows on hover
 */
function EraTooltip({ era, stats, details, icon, bonus, x, y }) {
  // Position tooltip
  const tooltipWidth = 220;
  let left = x - tooltipWidth / 2;
  let top = y;
  
  // Keep on screen
  if (left < 10) left = 10;
  if (left + tooltipWidth > window.innerWidth - 10) left = window.innerWidth - tooltipWidth - 10;
  
  return (
    <div className="era-tooltip" style={{ left, top }}>
      <div className="et-header">
        <span className="et-icon">{icon}</span>
        <div className="et-title">
          <div className="et-name">{getEraName(era)}</div>
          <div className="et-progress">{Math.floor(stats.percent)}% Complete</div>
        </div>
        {stats.complete && <span className="et-complete">✓</span>}
      </div>
      
      <div className="et-bar">
        <div className="et-bar-fill" style={{ width: `${stats.percent}%` }} />
      </div>
      
      <div className="et-stats">
        <div className="et-stat">
          <span className="et-stat-icon">🔬</span>
          <span className="et-stat-label">Techs:</span>
          <span className="et-stat-value">{stats.techs.done}/{stats.techs.total}</span>
        </div>
        {stats.wonders.total > 0 && (
          <div className="et-stat">
            <span className="et-stat-icon">🏛️</span>
            <span className="et-stat-label">Wonders:</span>
            <span className="et-stat-value">{stats.wonders.done}/{stats.wonders.total}</span>
          </div>
        )}
        <div className="et-stat">
          <span className="et-stat-icon">🏗️</span>
          <span className="et-stat-label">Buildings:</span>
          <span className="et-stat-value">{stats.buildings.done}/{stats.buildings.total}</span>
        </div>
      </div>
      
      {/* Quick preview of techs */}
      {details.techsResearched.length > 0 && (
        <div className="et-preview">
          <div className="et-preview-label">✓ Researched:</div>
          <div className="et-preview-items">
            {details.techsResearched.slice(0, 4).map(t => (
              <span key={t.id} className="et-preview-item done">{t.icon}</span>
            ))}
            {details.techsResearched.length > 4 && (
              <span className="et-preview-more">+{details.techsResearched.length - 4}</span>
            )}
          </div>
        </div>
      )}
      
      {details.techsPending.length > 0 && stats.unlocked && (
        <div className="et-preview">
          <div className="et-preview-label">○ Remaining:</div>
          <div className="et-preview-items">
            {details.techsPending.slice(0, 4).map(t => (
              <span key={t.id} className="et-preview-item pending">{t.icon}</span>
            ))}
            {details.techsPending.length > 4 && (
              <span className="et-preview-more">+{details.techsPending.length - 4}</span>
            )}
          </div>
        </div>
      )}
      
      <div className="et-bonus">
        <span className="et-bonus-icon">🎁</span>
        <span className="et-bonus-text">{bonus}</span>
      </div>
      
      <div className="et-hint">Click for details</div>
    </div>
  );
}

/**
 * Era Detail Panel - Centered popup modal with full-screen overlay
 */
function EraDetailPanel({ era, stats, details, icon, bonus, onClose }) {
  return ReactDOM.createPortal(
    <div className="era-detail-overlay" onClick={onClose}>
      <div className="era-detail-panel" onClick={e => e.stopPropagation()}>
        <div className="edp-header">
          <div className="edp-title">
            <span className="edp-icon">{icon}</span>
            <div>
              <div className="edp-name">{getEraName(era)}</div>
              <div className="edp-subtitle">Era {era} of {ERA_LIST.length}</div>
            </div>
          </div>
          <button className="edp-close" onClick={onClose}>✕</button>
        </div>
        
        <div className="edp-progress">
          <div className="edp-progress-bar">
            <div className="edp-progress-fill" style={{ width: `${stats.percent}%` }} />
          </div>
          <div className="edp-progress-text">{Math.floor(stats.percent)}% Complete</div>
        </div>
        
        <div className="edp-body">
          <div className="edp-sections">
            {/* Technologies */}
            <div className="edp-section">
              <div className="edp-section-header">
                <span className="edp-section-icon">🔬</span>
                <span className="edp-section-title">Technologies</span>
                <span className="edp-section-count">{stats.techs.done}/{stats.techs.total}</span>
              </div>
              <div className="edp-items">
                {details.techsResearched.map(t => (
                  <div key={t.id} className="edp-item done" title={t.description}>
                    <span className="edp-item-icon">{t.icon}</span>
                    <span className="edp-item-name">{t.name}</span>
                    <span className="edp-item-check">✓</span>
                  </div>
                ))}
                {details.techsPending.map(t => (
                  <div key={t.id} className={`edp-item pending ${stats.unlocked ? '' : 'locked'}`} title={t.description}>
                    <span className="edp-item-icon">{t.icon}</span>
                    <span className="edp-item-name">{t.name}</span>
                    <span className="edp-item-status">{stats.unlocked ? '○' : '🔒'}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Wonders */}
            {details.allWonders.length > 0 && (
              <div className="edp-section">
                <div className="edp-section-header">
                  <span className="edp-section-icon">🏛️</span>
                  <span className="edp-section-title">Wonders</span>
                  <span className="edp-section-count">{stats.wonders.done}/{stats.wonders.total}</span>
                </div>
                <div className="edp-items">
                  {details.wondersBuilt.map(w => (
                    <div key={w.id} className="edp-item done" title={w.description}>
                      <span className="edp-item-icon">{w.icon}</span>
                      <span className="edp-item-name">{w.name}</span>
                      <span className="edp-item-check">✓</span>
                    </div>
                  ))}
                  {details.wondersPending.map(w => (
                    <div key={w.id} className={`edp-item pending ${stats.unlocked ? '' : 'locked'}`} title={w.description}>
                      <span className="edp-item-icon">{w.icon}</span>
                      <span className="edp-item-name">{w.name}</span>
                      <span className="edp-item-status">○</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Buildings - Full width */}
            <div className="edp-section full-width">
              <div className="edp-section-header">
                <span className="edp-section-icon">🏗️</span>
                <span className="edp-section-title">Buildings Unlocked</span>
                <span className="edp-section-count">{stats.buildings.done}/{stats.buildings.total}</span>
              </div>
              <div className="edp-items compact">
                {details.allBuildings.map(b => {
                  const owned = (details.buildingsOwned || []).some(bo => bo.id === b.id);
                  return (
                    <div key={b.id} className={`edp-item-compact ${owned ? 'owned' : ''}`} title={`${b.name}: ${b.description}`}>
                      <span>{b.icon}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        
        <div className="edp-footer">
          <div className="edp-bonus">
            <span className="edp-bonus-icon">🎁</span>
            <span className="edp-bonus-label">Era Bonus:</span>
            <span className="edp-bonus-text">{bonus}</span>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default AgeProgressBar;
